import { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { Launch } from '@carbon/react/icons';
import { Helmet } from 'react-helmet-async';
import { Link } from 'components/link/link';
import { PageHeader } from 'components/page-header';
import { pageTitle } from 'utils/page-title';
import { formatResource } from 'utils/misc';
import { format8 } from 'utils/number';
import routes from '~/routes';
import { DivisionOrderSupportingFiles } from '../../show/supporting-files';
import styles from '../../show/page.module.scss';
import { useDivisionOrderLineItemAtom } from './atoms';

const Detail: FC<{ label: string; value: ReactNode }> = ({ label, value }) => {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.length === 0);
  return (
    <div className={styles.detail}>
      <dt className={styles.detailLabel}>{label}</dt>
      <dd className={styles.detailValue}>{empty ? '--' : value}</dd>
    </div>
  );
};

const DivisionOrderLineItemShowPage: FC = () => {
  const { divisionOrderId = '', id = '' } = useParams<{
    divisionOrderId: string;
    id: string;
  }>();
  const resource = useAtomValue(
    useDivisionOrderLineItemAtom(divisionOrderId, id)
  );
  const { loading, error, data: lineItem } = formatResource(resource);

  if (loading && !lineItem) {
    return <Loading description="Loading line item..." withOverlay={false} />;
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Unable to load division order line item"
        subtitle={error}
      />
    );
  }

  const name = lineItem?.name ?? id;
  const parentName = lineItem?.divisionOrder?.name ?? divisionOrderId;
  // Backend `salesforceHref` is absent for Provisioned (DOLI-P-*) line items
  // since they have no real SF record. Hide the action when there's no link.
  const salesforceUrl = lineItem?.salesforceHref ?? null;

  return (
    <>
      <Helmet>
        <title>{pageTitle(name)}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[
          [routes.divisionOrdersPath, 'Division Orders'],
          [lineItem?.divisionOrder?.href ?? '', parentName],
          ['', name],
        ]}
        title={name}
        actions={
          salesforceUrl ? (
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Launch}
              href={salesforceUrl}
              target="_blank"
              rel="noreferrer"
            >
              View in Salesforce
            </Button>
          ) : null
        }
      />

      <div className={styles.pageContent}>
        {/* ─── Line Item Details ─── */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Line Item Details</h3>
          <dl className={styles.detailsGrid}>
            <Detail
              label="Division Order"
              value={
                lineItem?.divisionOrder?.href ? (
                  <Link to={lineItem.divisionOrder.href}>
                    {lineItem.divisionOrder.name ?? '--'}
                  </Link>
                ) : (
                  lineItem?.divisionOrder?.name
                )
              }
            />
            <Detail label="Line Item Status" value={lineItem?.lineItemStatus} />
            <Detail
              label="Interest"
              value={
                lineItem?.interest !== null && lineItem?.interest !== undefined
                  ? format8(lineItem.interest)
                  : null
              }
            />
            <Detail label="Interest Type" value={lineItem?.interestType} />
            <Detail
              label="Property Name"
              value={
                lineItem?.propertyName ??
                lineItem?.property?.propertyName ??
                null
              }
            />
            <Detail label="CRM Account" value={lineItem?.crmAccount?.name} />
          </dl>
        </section>

        {/* ─── Supporting Files ─── */}
        {lineItem?.resources?.supportingFiles?.href && (
          <DivisionOrderSupportingFiles
            href={lineItem.resources.supportingFiles.href}
          />
        )}
      </div>
    </>
  );
};

export { DivisionOrderLineItemShowPage };
